/**
 * ShareButton — native share on mobile (WhatsApp, etc.), copy link on desktop.
 * Matches the visual style of ScheduleButton.
 */
import * as React from 'react';
import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { Theme } from '../Theme/Theme';
import { useTrans } from '../Store/System/LocalizationStore';
import { useRootStore } from '../Store/useRootStore';

const Container = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background-color: white;
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  margin-bottom: 16px;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${Theme.Colors.primary}30;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
`;

const TextWrapper = styled.div`
  flex: 1;
  text-align: center;
  margin: 0 8px;
`;

const PrimaryText = styled.div`
  font-weight: 600;
  color: ${Theme.Colors.black};
`;

const FeedbackText = styled.div`
  font-size: 0.85em;
  color: ${Theme.Colors.primary};
  margin-top: 2px;
`;

const Icon = styled.i`
  color: ${Theme.Colors.lightGrey};
  font-size: 1.2em;
`;

export type ShareData = {
  title: string;
  description?: string;
  imageUrl?: string;
  contentType?: string;
  contentId?: string;
};

export type ShareButtonProps = {
  data: ShareData;
};

export const ShareButton: React.FC<ShareButtonProps> = ({ data }) => {
  const { t } = useTrans();
  const { analytics } = useRootStore();
  const [copied, setCopied] = useState(false);

  const shareUrl = window.location.href;

  const handleShare = useCallback(async () => {
    analytics.logEvent('content_shared', {
      objectId: data.contentId,
      objectType: data.contentType,
      method: 'share' in navigator ? 'native' : 'clipboard',
    });

    // Native share (mobile — WhatsApp, Telegram, etc.)
    if ('share' in navigator && navigator.share) {
      try {
        await navigator.share({
          title: data.title,
          text: data.description || data.title,
          url: shareUrl,
        });
        return;
      } catch {
        // User cancelled or share failed — fall through to clipboard
      }
    }

    // Desktop fallback — copy link to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Last resort: prompt-based copy
      window.prompt('Copy this link:', shareUrl);
    }
  }, [data, shareUrl, analytics]);

  return (
    <Container onClick={handleShare}>
      <Icon className="bi bi-share" />
      <TextWrapper>
        <PrimaryText>
          {copied
            ? t('share.copied', { defaultValue: 'Link copied!' })
            : t('share.button', { defaultValue: 'Share' })}
        </PrimaryText>
        {copied && (
          <FeedbackText>{t('share.copiedHint', { defaultValue: 'Paste it anywhere' })}</FeedbackText>
        )}
      </TextWrapper>
      <Icon className={copied ? 'bi bi-check-lg' : 'bi bi-chevron-right'} />
    </Container>
  );
};
